"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Menu, X, ChevronRight, Phone, MapPin, Calendar, ClipboardList } from "lucide-react";
import Link from "next/link";
import { allServices } from "@/data/Static/services";

const menuLinks = [
  { href: "/", label: "Főoldal" },
  { href: "/rolunk", label: "Rólunk" },
  { href: "/arlista", label: "Árlista" },
];

export default function HamburgerMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      setShowServices(false);
    }
  }, [isOpen, mounted]);

  const toggleMenu = () => setIsOpen(!isOpen);

  if (!mounted) return (
    <button className="w-10 h-10 flex items-center justify-center rounded-full bg-hill-primary/5 text-hill-primary opacity-50 cursor-not-allowed">
      <Menu className="w-6 h-6" />
    </button>
  );

  return (
    <>
      <button
        onClick={toggleMenu}
        className="w-10 h-10 flex items-center justify-center rounded-full bg-hill-primary/5 text-hill-primary hover:bg-hill-primary/10 transition-all active:scale-95"
        aria-label={isOpen ? "Menü bezárása" : "Menü megnyitása"}
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {mounted && isOpen && createPortal(
        <div 
          className="fixed inset-0 z-[9999] bg-white flex flex-col"
          style={{ height: '100dvh', width: '100vw', top: 0, left: 0 }}
        >
            {/* Menu Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <span className="font-serif font-bold text-xl text-hill-primary uppercase tracking-widest">
                Menu
              </span>
              <button
                onClick={toggleMenu}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-hill-primary/5 text-hill-primary"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Menu Content */}
            <div className="flex-1 overflow-y-auto px-6 py-8">
              <nav className="flex flex-col gap-8">
                {menuLinks.map((link) => (
                  <div key={link.href}>
                    <Link
                      href={link.href}
                      onClick={toggleMenu}
                      className="text-2xl font-serif font-bold text-hill-primary hover:text-hill-secondary transition-colors"
                    >
                      {link.label}
                    </Link>
                  </div>
                ))}

                {/* Services Toggle */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + menuLinks.length * 0.05 }}
                >
                  <button
                    onClick={() => setShowServices(!showServices)}
                    className="w-full flex justify-between items-center text-2xl font-serif font-bold text-hill-primary hover:text-hill-secondary transition-colors text-left"
                  >
                    Szolgáltatások
                    <ChevronRight className={`w-8 h-8 transition-transform ${showServices ? "rotate-90" : ""}`} />
                  </button>
                  
                  <AnimatePresence>
                    {showServices && (
                      <motion.div
                        key="services-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden bg-hill-primary/5 rounded-2xl mt-4"
                      >
                        <div className="p-4 grid grid-cols-1 gap-2">
                          {allServices.map((service) => (
                            <Link
                              key={service.id}
                              href={service.href}
                              onClick={toggleMenu}
                              className="flex items-center gap-3 p-3 rounded-xl hover:bg-white transition-colors group"
                            >
                              <div className={`w-8 h-8 p-1.5 rounded-lg ${service.bgColor} ${service.color}`}>
                                {service.icon}
                              </div>
                              <span className="font-bold text-xs uppercase tracking-widest text-hill-primary group-hover:text-hill-secondary transition-colors">
                                {service.title}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Booking Section */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className=" p-6 bg-hill-primary text-white rounded-[32px] mt-4 shadow-lg shadow-hill-primary/20"
                >
                  <h3 className="font-serif font-bold text-lg mb-6 uppercase tracking-widest">Időpontfoglalás</h3>
                  <div className="flex flex-col gap-4">
                    <Link href="/gyogytorna-foglalas" onClick={toggleMenu} className="flex items-center gap-4 p-4 bg-white/10 rounded-2xl hover:bg-white/20 border border-white/10"><Calendar className="w-5 h-5 text-hill-secondary" /><span className="font-bold text-base text-white">Gyógytorna foglalás</span></Link>
                    <Link
                      href="/kapcsolatfelvetel"
                      onClick={toggleMenu}
                      className="flex items-center gap-4 p-4 bg-white/10 rounded-2xl hover:bg-white/20 transition-all border border-white/10"
                    >
                      <div className="w-10 h-10 rounded-full bg-hill-secondary/20 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-hill-secondary" />
                      </div>
                      <span className="font-bold text-base text-white">Fogászati foglalás</span>
                    </Link>
                    <Link
                      href="/logopedia-foglalas"
                      onClick={toggleMenu}
                      className="flex items-center gap-4 p-4 bg-white/10 rounded-2xl hover:bg-white/20 transition-all border border-white/10"
                    >
                      <div className="w-10 h-10 rounded-full bg-hill-secondary/20 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-hill-secondary" />
                      </div>
                      <span className="font-bold text-base text-white">Logopédia Foglalás</span>
                    </Link>
                    <Link
                      href="/logopedia-foglalas/manage"
                      onClick={toggleMenu}
                      className="flex items-center gap-4 p-4 bg-white/10 rounded-2xl hover:bg-white/20 transition-all border border-white/10"
                    >
                      <div className="w-10 h-10 rounded-full bg-hill-secondary/20 flex items-center justify-center">
                        <ClipboardList className="w-5 h-5 text-hill-secondary" />
                      </div>
                      <span className="font-bold text-base text-white">Foglalás kezelése</span>
                    </Link>
                  </div>
                </motion.div>
              </nav>
            </div>

        
        </div>,
        document.body
      )}
    </>
  );
}
